/**
 * <StudentCoachTwoPane> — « Messages » et la conversation ouverte, sur un grand écran (page).
 *
 * Le gabarit à deux volets (`<TwoPaneTemplate>`) habillé pour l'élève : à gauche « Messages »
 * (l'équipe et les prochains lives), bornée à la largeur du volet ; à droite la conversation avec
 * le tuteur ouvert, SANS retour, et sa ligne marquée dans l'équipe. Rien d'ouvert : le volet droit
 * le dit. Sur un téléphone, les deux écrans d'aujourd'hui : « Messages », puis la conversation
 * seule, avec son retour.
 *
 * Contrôlée : la conversation ouverte arrive en props, avec son tuteur (`conversation`, ou `null`).
 * Toucher un autre tuteur repart à l'appelant (`coach.onOpenMessages`), qui ouvre la sienne ; le
 * retour du téléphone, aussi (`conversation.onBack`).
 */
import React from "react"

import { EmptyState } from "../../molecules/EmptyState"
import { TWO_PANE_LIST_WIDTH, TwoPaneTemplate } from "../../templates/TwoPaneTemplate"
import {
    StudentTutorConversationPage,
    type StudentTutorConversationPageProps,
} from "../StudentTutorConversationPage/StudentTutorConversationPage"
import { StudentCoachPage, type StudentCoachPageProps } from "./StudentCoachPage"

const TEXTS = {
    emptyTitle: "Aucune conversation ouverte",
    emptyMessage: "Choisis un tuteur dans la liste pour lui écrire.",
}

export type StudentCoachTwoPaneProps = {
    /** « Messages », tel quel : la liste. */
    coach: StudentCoachPageProps
    /** La conversation ouverte, avec son tuteur. `null` : aucune. Son `onBack` ne sert que sur un téléphone. */
    conversation: StudentTutorConversationPageProps | null
    testID?: string
}

export function StudentCoachTwoPane(props: StudentCoachTwoPaneProps) {
    const { coach, conversation, testID } = props

    return (
        <TwoPaneTemplate
            testID={testID}
            list={(split) => (
                <StudentCoachPage
                    {...coach}
                    openTutorId={split ? (conversation?.tutor.id ?? null) : null}
                    columnWidth={split ? TWO_PANE_LIST_WIDTH : coach.columnWidth}
                />
            )}
            detail={
                conversation == null
                    ? null
                    : (split) => (
                          <StudentTutorConversationPage
                              {...conversation}
                              onBack={split ? undefined : conversation.onBack}
                          />
                      )
            }
            empty={<EmptyState iconName="comment-dots" title={TEXTS.emptyTitle} message={TEXTS.emptyMessage} />}
        />
    )
}
